import { TeoPersistableWritableStore, type ExtractWritablePropertyKeys } from '../state/TeoStore';
import type { PromptOptionTemplate } from '../TeoApi';
import { TeoCore } from '../TeoCore';
import { optionsToDropdownCategories } from './generator/GeneratorUiHandler.helpers';
import { TeoText, type SelectedOptions } from './TeoText';
import { areAllPromptOptionsSelected } from './TeoText.helpers';
import type { TeoView } from './TextUi';

export type PerformanceMode = 'manual' | 'autoAI';

class TeoTextState {
  view: TeoView;
  previousView: Maybe<TeoView>;
  selectedPromptOptions: SelectedOptions;

  currentTemplateId: Maybe<string>;

  /** How many operations are loading right now. Not persisted. */
  loadingCount = 0;
  /** Text shown under the loading animation. Pure candy, may be empty. Not persisted. */
  loadingMessage = '';

  performanceMode: PerformanceMode;

  constructor(initialState: Partial<TeoTextState>) {
    this.view = initialState.view ?? 'performance';
    this.selectedPromptOptions = initialState.selectedPromptOptions ?? {};
    this.currentTemplateId = initialState.currentTemplateId ?? null;
    this.previousView = initialState.previousView ?? null;
    this.performanceMode = initialState.performanceMode ?? 'manual';
  }

  get isLoading(): boolean {
    return this.loadingCount > 0;
  }

  get allPromptOptionsSelected(): boolean {
    return areAllPromptOptionsSelected(this.currentTemplate, this.selectedPromptOptions);
  }

  get dropdownCategories() {
    return optionsToDropdownCategories(this.currentTemplate?.options) ?? [];
  }

  get currentTemplate(): PromptOptionTemplate | null {
    return getTemplateById(this.currentTemplateId);
  }
}

export interface TeoTextStoredState {
  view: TeoView;
  previousView: Maybe<TeoView>;
  selectedPromptOptions: SelectedOptions;
  currentTemplateId?: Maybe<string>;
  performanceMode: PerformanceMode;
}

export class TeoTextStore extends TeoPersistableWritableStore<TeoTextState, TeoTextStoredState> {
  #text: TeoText;
  #removeTemplateListener: () => void;
  constructor(text: TeoText, initialState: TeoTextStoredState) {
    super(
      new TeoTextState({
        view: initialState.view,
        selectedPromptOptions: initialState.selectedPromptOptions ?? {},
        currentTemplateId: initialState.currentTemplateId ?? null,
        previousView: initialState.previousView ?? null,
        performanceMode: initialState.performanceMode ?? 'manual',
      }),
    );
    this.#text = text;
    this.updateCurrentTemplateId();

    this.#removeTemplateListener = this.#text.core.dynamicConfig.addEventListener(
      'promptOptionTemplates',
      () => {
        this.updateCurrentTemplateId();
      },
    );
  }

  dispose(): void {
    this.#removeTemplateListener();
  }

  override setStateValue<K extends ExtractWritablePropertyKeys<TeoTextState>>(
    prop: K,
    value: TeoTextState[K],
    force = false,
  ) {
    if (prop === 'view' && value !== this.state.view) {
      super.setStateValue('previousView', value as Maybe<TeoView>);
    }
    return super.setStateValue(prop, value, force);
  }

  /**
   * Marks an operation as loading, so the editor shows its loading animation. Use
   * {@link withLoading} unless the start and the end of the operation are in different
   * places.
   * @param message Optional text to show while the operation runs.
   * @returns The function that ends this load. Calling it more than once does nothing.
   */
  startLoading(message = ''): () => void {
    const previousMessage = this.state.loadingMessage;
    this.updateStateValue('loadingCount', (count) => count + 1);
    if (message) {
      this.setStateValue('loadingMessage', message);
    }
    let ended = false;
    return () => {
      if (ended) return;
      ended = true;
      this.updateStateValue('loadingCount', (count) => Math.max(count - 1, 0));
      if (this.state.loadingCount === 0) {
        this.setStateValue('loadingMessage', '');
      } else if (message && this.state.loadingMessage === message) {
        // an operation this one was nested in is still running, so its message comes back
        this.setStateValue('loadingMessage', previousMessage);
      }
    };
  }

  /**
   * Runs an operation with the loading animation on, whatever its outcome.
   * @param operation The operation to run.
   * @param message Optional text to show while it runs.
   */
  async withLoading<T>(operation: () => Promise<T>, message = ''): Promise<T> {
    const endLoading = this.startLoading(message);
    try {
      return await operation();
    } finally {
      endLoading();
    }
  }

  selectOption(id: string, value: string) {
    this.updateStateValue('selectedPromptOptions', (current) => ({
      ...current,
      [id]: value,
    }));
  }

  selectTemplate(id: string) {
    this.setStateValue('currentTemplateId', id);
  }

  toStorableState(): TeoTextStoredState {
    return structuredClone({
      view: this.state.view,
      previousView: this.state.previousView,
      selectedPromptOptions: this.state.selectedPromptOptions,
      currentTemplateId: this.state.currentTemplateId,
      performanceMode: this.state.performanceMode,
    });
  }

  private updateCurrentTemplateId() {
    const templates = TeoCore.current.dynamicConfig.options.promptOptionTemplates;
    if (!templates?.length) return;

    if (this.state.currentTemplateId) {
      const template = templates.find((t) => t.id === this.state.currentTemplateId);
      if (template) {
        // found a template update auto selected options
        const autoSelectedOptions = Object.entries(template.options)
          .filter(([, options]) => options.length === 1) // only one option, so candidate for auto selection
          .map(([categoryId, options]) =>
            options[0] ? ([categoryId, String(options[0].id)] as const) : undefined,
          )
          .filter((x) => !!x);

        if (autoSelectedOptions.length) {
          this.updateStateValue('selectedPromptOptions', (current) => ({
            ...current,
            ...Object.fromEntries(autoSelectedOptions),
          }));
        }

        // svelte needs the '*' event
        this.notifyChanged('currentTemplateId');
        return;
      }
    }
    // templateId does not exists or none was selected yet
    this.setStateValue('currentTemplateId', templates[0]?.id, true);
  }
}

function getTemplateById(id: Maybe<string>) {
  if (!id) return null;
  return (
    TeoCore.current.dynamicConfig.options.promptOptionTemplates?.find((t) => t.id === id) ?? null
  );
}
